import React, {useEffect, useState} from "react";
import {Box} from "@mui/material";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {CampaignRepository} from "../../../repositories/CampaignRepository";
import useConfirm from "../../../hooks/useConfirm";
import {CampaignsHeader} from "./components/CampaignsHeader";
import {CampaignsTable} from "./components/CampaignsTable";
import {CampaignFormDialog} from "./components/campaign-form/CampaignFormDialog";
import {useAmiExtensions} from "./hooks/useAmiExtensions";
import {useCollectionView} from "../../../hooks/useCollectionView";

export const CampaignsPage = ({project}) => {
  const [viewMode, setViewMode] = useCollectionView("campaigns-view-preference", "cards");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();
  const {getExtension} = useAmiExtensions(project?.id);
  const confirmDelete = useConfirm("Delete campaign?", "The campaign and all its phone numbers will be deleted.", "Delete");

  const fetchCampaigns = () => {
    if (!project?.id) return;
    setLoading(true);
    CampaignRepository.getAll(project.id)
      .then(response => setCampaigns(response?.data || []))
      .catch(error => {
        console.error(error);
        toast.error("Failed to load campaigns");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchCampaigns();
  }, [project?.id]);

  const handleDelete = async (campaign) => {
    const confirmed = await confirmDelete({title: `Delete campaign "${campaign.name}"?`});
    if (!confirmed) return;
    CampaignRepository.delete(campaign.id)
      .then(() => {
        toast.success("Campaign deleted");
        setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
      })
      .catch(() => toast.error("Failed to delete campaign"));
  };

  const openCreate = () => {
    setEditing(null);
    setOpenForm(true);
  };

  const openEdit = (campaign) => {
    setEditing(campaign);
    setOpenForm(true);
  };

  const handleSaved = (saved) => {
    if (!saved) return fetchCampaigns();
    setCampaigns(prev => prev.some(c => c.id === saved.id)
      ? prev.map(c => c.id === saved.id ? saved : c)
      : [saved, ...prev]);
  };

  return (
    <Box maxWidth={"xl"} sx={{height: "100%", width: "90%", mx: "auto", display: "flex", flexDirection: "column", gap: 2, p: 2}}>
      <CampaignFormDialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        flowUid={project?.id}
        campaign={editing}
        onSaved={handleSaved}
      />
      <CampaignsHeader onCreate={openCreate} viewMode={viewMode} onViewChange={setViewMode}/>
      <CampaignsTable
        viewMode={viewMode}
        campaigns={campaigns}
        loading={loading}
        getExtension={getExtension}
        onOpen={(campaign) => navigate(`/project/${project?.id}/campaigns/${campaign.id}`)}
        onEdit={openEdit}
        onDelete={handleDelete}
      />
    </Box>
  )
}
